/* eslint-disable max-lines-per-function */
import React from "react";
import { useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
import { Form, Formik } from "formik";
import { DialogContent, Grid } from "@material-ui/core";
import * as yup from "yup";
import { useSnackbar } from "components/haven-design/snackbar";
import { SubmitButton } from "components/buttons";
import { PossibleClientRemovalReasons, FormikStructuredReasons } from "components/structured-reasons";
import { Button, Modal } from "components/haven-design";
import logger from "services/logger";
import { DialogActions } from "components/haven-design/modal/dialog";
import { removeSelfFromList as removeSelfFromListType, removeSelfFromListVariables } from "types/gql-types";

const REMOVE_SELF_FROM_LIST = gql`
  mutation removeSelfFromList($interestId: ID!, $reason: String!, $waitlistCode: String) {
    removeSelfFromList(interestId: $interestId, reason: $reason, waitlistCode: $waitlistCode)
  }
`;

const formSchema = yup.object().shape({
  reason: yup.string().trim().required(),
});

const initialData = { reason: "" };

interface Props {
  isOpen: boolean;
  onRequestClose: (isRemoved: boolean) => void;
  interestId: string;
  interestListName: string;
  removalReasons?: string[];
  waitlistCode?: string;
}

const RemoveUserModal: React.FC<Props> =
  ({ removalReasons = PossibleClientRemovalReasons, ...props }) => {
    const { openSnackbar } = useSnackbar();

    const [removeSelfFromList, { loading }] =
      useMutation<removeSelfFromListType, removeSelfFromListVariables>(REMOVE_SELF_FROM_LIST);
    const submitForm = (
      formData: {reason: string },
    ) => {
      removeSelfFromList(
        { variables: { interestId: props.interestId,
          waitlistCode: props.waitlistCode,
          ...formData } },
      )
        .then(() => {
          openSnackbar({ message: "Successfully removed from the list", status: "success" });
          props.onRequestClose(true);
        })
        .catch((err) => {
          logger.sendError(err);
          openSnackbar({ message: "Failed to remove from list", status: "error" });
        });
    };

    const formId = `remove-me-${props.interestId}`;

    return (
      <Modal
        fullWidth
        title = {
          <>
            Why do you want to be removed from&nbsp;
            <strong>{props.interestListName}</strong>?
          </>
        }
        isOpen={props.isOpen}
        onRequestClose={() => props.onRequestClose(false)}>
        <Formik
          initialValues={initialData}
          onSubmit={submitForm}
          validationSchema={formSchema}>
          {({ isValid }) => (
            <>
              <DialogContent>
                <Form id={formId}>
                  <Grid container>
                    <Grid item xs={12}>
                      <FormikStructuredReasons
                        name="reason"
                        label="Removal Reason*"
                        options={removalReasons}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <p style={{ paddingTop: 16 }}>This action cannot be undone</p>
                    </Grid>

                  </Grid>
                </Form>
              </DialogContent>
              <DialogActions>
                <Button
                  color="secondary"
                  size="S"
                  onClick={() => props.onRequestClose(false)}
                >
                  Cancel
                </Button>
                <SubmitButton
                  form={formId}
                  text="Remove Me"
                  loadingText="Sending"
                  isLoading={loading}
                  disabled={!isValid}
                />
              </DialogActions>
            </>
          )}
        </ Formik>
      </Modal>
    );
  };

export default RemoveUserModal;
